import React, { useState, useEffect } from 'react';
import { Form, Button, ListGroup } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Loader from '../../components/Loader';
import Message from '../../components/Message';
import {
  useGetIncomeDetailsQuery,
  useUploadIncomeImagesMutation,
  useUpdateIncomeMutation,
} from '../../slices/incomesApiSlice';
import { useGetCustomersQuery } from '../../slices/customersApiSlice';

const NetCashEdit = () => {
  const { id: incomeId } = useParams();
  const navigate = useNavigate();

  // State management
  const [formData, setFormData] = useState({
    date: '',
    account: '',
    accountName: '',
    subAccount: '',
    details: '',
    amount: '',
    category: '',
    notes: '',
  });
  const [images, setImages] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const { data: income, isLoading, error } = useGetIncomeDetailsQuery(incomeId);
  const { data: customersData } = useGetCustomersQuery({ keyword: customerSearch });
  const [updateIncome] = useUpdateIncomeMutation();
  const [uploadIncomeImages] = useUploadIncomeImagesMutation();

  useEffect(() => {
    if (income) {
      setFormData({
        date: income.date ? new Date(income.date).toISOString().split('T')[0] : '',
        account: income.account || '',
        accountName: income.accountName || '',
        subAccount: income.subAccount || '',
        details: income.details || '',
        amount: income.amount || '',
        category: income.category || '',
        notes: income.notes || '',
      });
      setImages(income.images || []);
    }
  }, [income]);

  useEffect(() => {
    if (customerSearch.length >= 3 && customersData?.customers) {
      setFilteredCustomers(
        customersData.customers.filter((customer) =>
          customer.name.toLowerCase().includes(customerSearch.toLowerCase())
        )
      );
    } else {
      setFilteredCustomers([]);
    }
  }, [customerSearch, customersData]);

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setFormData((prevData) => ({
      ...prevData,
      accountName: customer.name,
    }));
    setFilteredCustomers([]);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    if (name === 'accountName') setCustomerSearch(value);
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    try {
      await updateIncome({
        incomeId,
        ...formData,
        images,
        accountName: selectedCustomer?.name || formData.accountName,
      }).unwrap();
      toast.success('Income updated successfully');
      navigate('/netcash');
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };

  const validateFiles = (files) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    const maxSize = 5 * 1024 * 1024; // 5MB
    for (let file of files) {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`File type not allowed: ${file.type}`);
        return false;
      }
      if (file.size > maxSize) {
        toast.error(`File size exceeds 5MB: ${file.name}`);
        return false;
      }
    }
    return true;
  };

  const uploadImageHandler = async (files) => {
    if (!validateFiles(files)) return;

    const formData = new FormData();
    Array.from(files).forEach((file) => formData.append('images', file));

    try {
      const res = await uploadIncomeImages(formData).unwrap();
      toast.success(res.message);
      setImages([...images, ...res.images]);
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };

  const removeImageHandler = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const [calcDisplay, setCalcDisplay] = useState('');
    const handleCalculatorClick = (value) => {
    if (value === '=') {
      try {
        setCalcDisplay(eval(calcDisplay).toString());
      } catch {
        setCalcDisplay('Error');
      }
    } else if (value === 'C') {
      setCalcDisplay('');
    } else {
      setCalcDisplay((prev) => prev + value);
    }
  };
  

  return (
    <div className="container mt-4">
      <h1>Edit Income</h1>
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">
          {error?.data?.message || 'Error loading income details'}
        </Message>
      ) : (
        <Form onSubmit={submitHandler}>
          <Form.Group controlId="date" className="mb-3">
            <Form.Label>Date</Form.Label>
            <Form.Control
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              required
            />
          </Form.Group>
  
          <Form.Group controlId="account" className="mb-3">
            <Form.Label>Account Type</Form.Label>
            <Form.Control
              as="select"
              name="account"
              value={formData.account}
              onChange={handleChange}
              required
            >
              <option value="Credit">Credit</option>
              <option value="Debit">Debit</option>
            </Form.Control>
          </Form.Group>
  
          <Form.Group controlId="accountName" className="mb-3">
            <Form.Label>Account Name</Form.Label>
            <Form.Control
              type="text"
              name="accountName"
              value={formData.accountName}
              placeholder="Search account name"
              onChange={handleChange}
              required
            />
            {customerSearch.length >= 3 && filteredCustomers.length > 0 && (
              <ListGroup className="dropdown-menu mt-2">
                {filteredCustomers.map((customer) => (
                  <ListGroup.Item
                    key={customer._id}
                    action
                    onClick={() => handleCustomerSelect(customer)}
                  >
                    {customer.name} ({customer.company || 'No company'})
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </Form.Group>
  
          <Form.Group controlId="subAccount" className="mb-3">
            <Form.Label>Sub-Account</Form.Label>
            <Form.Control
              as="select"
              name="subAccount"
              value={formData.subAccount}
              onChange={handleChange}
              required
            >
              <option value="NET REVENUE">NET REVENUE</option>
              <option value="LAB">LAB</option>
              <option value="OTHER INCOME">OTHER INCOME</option>
              <option value="SERVICES">SERVICES</option>         
              <option value="LOAN">PAYMENT</option>
              
            </Form.Control>
          </Form.Group>
  
          <Form.Group controlId="details" className="mb-3">
            <Form.Label>Details</Form.Label>
            <Form.Control
              type="text"
              name="details"
              value={formData.details}
              placeholder="Enter details"
              onChange={handleChange}
              required
            />
          </Form.Group>
  
          <Form.Group controlId="amount" className="mb-3">
            <Form.Label>Amount</Form.Label>
            <Form.Control
              type="number"
              name="amount"
              value={formData.amount}
              placeholder="Enter amount"
              onChange={handleChange}
              required
            />
          </Form.Group>
          {/* Calculator UI */}
                  <div className="mb-4 p-3 border rounded" style={{ maxWidth: '230px' }}>
                    <Form.Label>Mini Calculator</Form.Label>
                    <Form.Control type="text" value={calcDisplay} readOnly className="mb-2" />
                    {['7','8','9','/','4','5','6','*','1','2','3','-','0','.','=','+','C'].map((char) => (
                      <Button
                        key={char}
                        variant="light"
                        className="m-1"
                        onClick={() => handleCalculatorClick(char)}
                        style={{ width: '40px' }}
                      >
                        {char}
                      </Button>
                    ))}
                    <div className="mt-2">
                      <Button
                        variant="outline-primary"
                        onClick={() =>
                          setFormData((prev) => ({ ...prev, amount: calcDisplay }))
                        }
                        disabled={calcDisplay === '' || calcDisplay === 'Error'}
                      >
                        Copy to Amount Field
                      </Button>
                    </div>
                    </div>
  
          <Form.Group controlId="category" className="mb-3">
                              <Form.Label>Category</Form.Label>
                              <Form.Control
                                as="select"
                                name="category"
                                value={formData.category}
                                onChange={handleChange}
                              >
                                <option value="Morning">Morning</option>
                                <option value="AfterNoon">AfterNoon</option>
                                <option value="Night">Night</option>
                                <option value="Other">Other</option>
                              </Form.Control>
                            </Form.Group>
                  
          <Form.Group controlId="notes" className="mb-3">
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              name="notes"
              value={formData.notes}
              placeholder="Enter additional notes"
              rows={3}
              onChange={handleChange}
            />
          </Form.Group>
  
          <Form.Group controlId="images" className="mb-3">
            <Form.Label>Images</Form.Label>
            <Form.Control
              type="file"
              multiple
              onChange={(e) => uploadImageHandler(e.target.files)}
            />
            <div className="mt-3">
              {images.map((img, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'inline-block',
                    position: 'relative',
                    marginRight: '10px',
                  }}
                >
                  <img
                    src={img}
                    alt={`Uploaded ${idx}`}
                    className="img-fluid"
                    style={{ maxWidth: '100px', height: '100px', objectFit: 'cover' }}
                  />
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => removeImageHandler(idx)}
                    style={{
                      position: 'absolute',
                      top: '0',
                      right: '0',
                      borderRadius: '50%',
                      padding: '2px 5px',
                    }}
                  >
                    ✖
                  </Button>
                </div>
              ))}
            </div>
          </Form.Group>
  
          <Button type="submit" variant="primary">
            Update Income
          </Button>
        </Form>
      )}
    </div>
  );
};

export default NetCashEdit;